/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import { mapBy, resolved } from '@linc-technologies/linced-common/utils/arrays';

export default class TableController extends Controller {
  // Services
  @service
  features;

  @service
  options;

  @service
  store;

  @service
  strings;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @alias('model.subject.isGradable')
  gradingEnabled;

  @tracked sortProp = 'fullName';
  @tracked sortDir = 'asc';
  queryParams = ['topicTab'];

  @alias('model.group.currentLearners.length')
  numLearners;

  @tracked
  goalDialogOpen;

  @tracked
  selectedGoal;

  @tracked
  selectedGoalStates;

  @tracked
  tab;

  @computed('model.group.currentLearners.[]', 'sortProp', 'sortDir')
  get sortedLearners() {
    let { sortProp, sortDir } = this;

    // Combine all goal sources into one array for lookup
    let allGoals = [
      ...Object.values(this.sortedCoreGoals || {}).flat(),
      ...Object.values(this.sortedPriorGoals || {}).flat(),
      ...Object.values(this.sortedFutureGoals || {}).flat(),
    ];

    let learners = this.model.group.currentLearners.map((learner) => {
      let sortValue;

      if (sortProp.startsWith('goal_')) {
        let goalId = sortProp.replace('goal_', '');

        // Find the goal across all goal sources
        let goal = allGoals.find((g) => g.id === goalId);

        if (goal) {
          // Extract the relevant goal state for the learner
          // resolve the async hasMany relationship upfront.
          let goalState = resolved(goal.states).find(
            (state) => state.person_id === learner.personId,
          );

          if (goalState) {
            // Determine sorting value based on awarded state
            switch (goalState.awarded) {
              case GoalState.SECURE:
                sortValue = 'a-secure'; // use a-secure that goals are sorted by these first
                break;
              case GoalState.DEVELOPING:
                sortValue = 'developing';
                break;
              default:
                sortValue = 'not-set';
            }
          } else {
            sortValue = 'unknown'; // Default if no state matches
          }
        } else {
          sortValue = 'unknown'; // No goal found
        }
      } else {
        // Default sorting by learner's properties
        sortValue = learner[sortProp] || '';
      }

      return { learner, sortValue };
    });

    // Sort learners based on sortValue
    learners = learners
      .slice()
      .sort((a, b) => (a.sortValue || '').localeCompare(b.sortValue || ''));

    // Reverse for descending order
    return sortDir === 'asc'
      ? mapBy(learners, 'learner')
      : mapBy(learners.reverse(), 'learner');
  }

  @computed(
    'model.{currentGoal,currentGoal.states.[],group.currentLearners.[]}',
  )
  get learnersByGrade() {
    let currentGoal = this.get('model.currentGoal');
    if (!currentGoal) {
      return {};
    }

    let currentGoalId = currentGoal.goal.id;
    let learners = this.get('model.group.currentLearners') || [];
    let states = currentGoal.states || [];

    let groupedLearners = {};

    learners.forEach((learner) => {
      let goalState = states.find((state) => {
        return (
          state.person_id === learner.personId &&
          state.goal_id === currentGoalId
        );
      });

      let grade = (goalState && goalState.grade) || 'Not Graded';

      if (!groupedLearners[grade]) {
        groupedLearners[grade] = [];
      }

      groupedLearners[grade].push(learner);
    });

    return groupedLearners;
  }

  @computed('model.currentGoal.goal')
  get topicGoal() {
    let goal = this.get('model.currentGoal.goal') || [];
    return this.groupTopicBySubcategory(goal);
  }

  @computed('model.currentGoal.goal.linkedGoals.content.[]', 'topicTab')
  get sortedCoreGoalsBySubjectType() {
    let linkedGoals = this.get('model.currentGoal.goal.linkedGoals') || [];

    return this.groupGoalsBySubcategoryAndType(linkedGoals, this.topicTab);
  }

  @computed('model.currentGoal.goal.linkedGoals.content.[]')
  get sortedCoreGoals() {
    let linkedGoals = this.get('model.currentGoal.goal.linkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.currentGoal.goal.priorLinkedGoals.content.[]')
  get sortedPriorGoals() {
    let linkedGoals = this.get('model.currentGoal.goal.priorLinkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.currentGoal.goal.futureLinkedGoals.content.[]')
  get sortedFutureGoals() {
    let linkedGoals =
      this.get('model.currentGoal.goal.futureLinkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  // Helper method to group goals by subcategory label
  groupTopicBySubcategory(goal) {
    let subcategoryLabel = goal.get('subcategory.label') || 'Other';
    let acc = [];
    if (!acc[subcategoryLabel]) {
      acc[subcategoryLabel] = [];
    }
    // Initialise the total count if it doesn't exist
    if (!acc.totalCount) {
      acc.totalCount = 0;
    }
    // Add the goal to the group, increment the subcategory count, and the total count
    acc[subcategoryLabel].push(goal);
    acc.totalCount += 1;
    return acc;
  }

  groupGoalsBySubcategoryAndType(goals, type) {
    // ensure goals are resolved upfront.
    return resolved(goals).reduce((acc, goal) => {
      let subcategoryLabel = goal.get('subcategory.label') || 'Other';
      let subject = goal.get('subject');

      // Check if the subject exists and matches the desired type
      if (subject && subject.get('subjectTypeName') === type) {
        if (!acc[subcategoryLabel]) {
          acc[subcategoryLabel] = [];
        }

        // Initialize the total count if it doesn't exist
        if (!acc.totalCount) {
          acc.totalCount = 0;
        }

        // Add the goal to the group and increment the total count
        acc[subcategoryLabel].push(goal);
        acc.totalCount += 1;
      }

      return acc;
    }, {});
  }

  groupGoalsBySubcategory(goals) {
    // ensure goals are resolved upfront.
    return resolved(goals).reduce((acc, goal) => {
      let subcategoryLabel = goal.get('subcategory.label') || 'Other';

      if (!acc[subcategoryLabel]) {
        acc[subcategoryLabel] = [];
      }
      // Initialise the total count if it doesn't exist
      if (!acc.totalCount) {
        acc.totalCount = 0;
      }
      // Add the goal to the group, increment the subcategory count, and the total count
      acc[subcategoryLabel].push(goal);
      acc.totalCount += 1;

      return acc;
    }, {});
  }

  @computed('sortedCoreGoals', 'sortedPriorGoals', 'sortedFutureGoals')
  get combinedGoals() {
    let combinedGoals = [];
    let allSubcategories = new Set([
      ...Object.keys(this.sortedCoreGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
      ...Object.keys(this.sortedPriorGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
      ...Object.keys(this.sortedFutureGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
    ]);

    // Build each row with subcategory and the three types of goals
    allSubcategories.forEach((subcategory) => {
      combinedGoals.push({
        subcategory,
        priorGoals:
          this.sortedPriorGoals && this.sortedPriorGoals[subcategory]
            ? this.sortedPriorGoals[subcategory]
            : [],
        coreGoals:
          this.sortedCoreGoals && this.sortedCoreGoals[subcategory]
            ? this.sortedCoreGoals[subcategory]
            : [],
        futureGoals:
          this.sortedFutureGoals && this.sortedFutureGoals[subcategory]
            ? this.sortedFutureGoals[subcategory]
            : [],
      });
    });

    return combinedGoals;
  }

  @action
  updateSort(sortProp) {
    if (this.sortProp === sortProp) {
      // Toggle sort direction
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new sort property and reset direction
      this.sortProp = sortProp;
      this.sortDir = 'asc';
    }
  }

  @action
  updateTab(newTab) {
    this.tab = newTab;
  }

  @action
  noOp() {}

  @action
  openGoalDialog(goal, event) {
    event.preventDefault();
    this.selectedGoal = goal;
    // Although we already have the states, providing a fresh query allows the group-goal-setter to refresh
    // just the states for this goal, rather than having to refresh the states for ALL goals in the subject
    // every time a goal state is changed.  Closing the dialog will perform the full reload.
    this.selectedGoalStates = this.store.query('goals/state', {
      groupId: this.model.group.id,
      goalId: goal.id,
      current: true,
    });
    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
    this.model.currentGoal.states.update();
  }

  @action
  refreshStates() {
    return this.model.currentGoal.states.update();
  }
}
